import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Grid,
  Chip,
  Avatar,
  AvatarGroup,
  IconButton,
  Tooltip,
  Button,
  CircularProgress,
  Divider,
  Card,
  CardContent
} from '@mui/material';
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import {
  Clock,
  MessageCircle,
  Download,
  Filter,
  ChevronRight,
  Search
} from 'lucide-react';

import consultService from '../services/AGTUps-product-services/consultService';

/** 
 * Paleta de colores / theme 
 * similar a los otros shortcuts
 */
const COLORS = {
  primary: 'rgba(128, 128, 128, 0.5)',
  text: {
    light: '#FFFFFF',
    dark: '#FFFFFF'
  }
};

const createCustomTheme = (mode, actualMode) =>
  createTheme({
    palette: {
      mode,
      actualMode
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
            textTransform: 'none',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none'
            }
          },
          contained: {
            backgroundColor: actualMode === 'wallpaper' ? '#FFFFFF' : COLORS.primary,
            color: actualMode === 'wallpaper' ? '#000000' : '#ffffff',
            '&:hover': {
              backgroundColor: actualMode === 'wallpaper' ? 'rgba(255, 255, 255, 0.85)' : COLORS.primary
            },
            '&:disabled': {
              backgroundColor:
                mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.12)'
                  : 'rgba(0, 0, 0, 0.12)'
            }
          }
        }
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: '16px',
            border: '1px solid',
            borderColor: actualMode === 'wallpaper'
              ? 'rgba(128, 128, 128, 0.3)'
              : (mode === 'light' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.12)'),
            backgroundColor: actualMode === 'wallpaper' ? 'rgba(128, 128, 128, 0.3)' : undefined,
            backdropFilter: actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
            WebkitBackdropFilter: actualMode === 'wallpaper' ? 'blur(10px)' : 'none'
          }
        }
      }
    }
  });

const StatusChip = ({ status }) => {
  let color = 'default';
  let label = status;

  if (status === 'finalizado') {
    color = 'success';
    label = 'Completada';
  } else if (status === 'programado') {
    color = 'primary';
    label = 'Programada';
  } else if (status === 'finalizado_timeout') {
    color = 'warning';
    label = 'Finalizada por timeout';
  }

  return (
    <Chip 
      label={label} 
      color={color}
      size="small"
      sx={{ fontSize: '0.7rem' }}
    />
  );
};

// ======== Componente Shortcut: RecentConsultationsShortcut ========
function RecentConsultationsShortcut({ open, onClose }) {
  // Tomamos el modo del theme "padre"
  const parentTheme = useTheme();
  const customTheme = createCustomTheme(parentTheme.palette.mode, parentTheme.palette.actualMode);
  const isDark = parentTheme.palette.mode === 'dark';

  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Cargar consultas cuando se abre el shortcut
  useEffect(() => {
    if (open) {
      fetchConsultations();
    }
  }, [open]);

  const fetchConsultations = async () => {
    setLoading(true);
    try {
      const res = await consultService.getUserConsultations();
      if (res.success && res.data) {
        // Ordenar consultas por fecha más reciente primero
        const sortedConsultations = res.data.sort((a, b) => 
          new Date(b.updatedAt) - new Date(a.updatedAt)
        );
        setConsultations(sortedConsultations);
      }
    } catch (error) {
      console.error('Error fetching recent consultations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Formatear fecha
  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const handleViewDetail = (consultation) => {
    setSelectedConsultation(consultation);
    setIsModalOpen(true);
  };

  const closeDetailModal = () => {
    setIsModalOpen(false);
    setSelectedConsultation(null);
  };

  // Renderizar el contenido principal del shortcut
  const renderMainContent = () => {
    if (loading) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (consultations.length === 0) {
      return (
        <Box 
          sx={{ 
            textAlign: 'center', 
            py: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2
          }}
        >
          <MessageCircle size={48} opacity={0.5} />
          <Typography variant="body1" color="text.secondary">
            No hay consultas recientes.
          </Typography>
          <Button 
            variant="contained" 
            color="primary"
            startIcon={<MessageCircle />}
            onClick={onClose}
          >
            Iniciar una consulta
          </Button>
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Tus consultas recientes
        </Typography>
        
        <Grid container spacing={2}>
          {consultations.slice(0, 6).map((consult, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card 
                sx={{ 
                  borderRadius: 2,
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 4,
                  }
                }}
                onClick={() => handleViewDetail(consult)}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {consult.title || `Consulta del ${formatDate(consult.createdAt)}`}
                    </Typography>
                    <StatusChip status={consult.status} />
                  </Box>
                  
                  <Box display="flex" alignItems="center" sx={{ mb: 1.5 }}>
                    <Clock size={14} style={{ marginRight: 6, opacity: 0.7 }} />
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(consult.updatedAt)}
                    </Typography>
                  </Box>
                  
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                      mb: 1.5,
                      borderLeft: `3px solid ${COLORS.primary}`,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {consult.conversation && consult.conversation.length > 0 && consult.conversation[0].messages && consult.conversation[0].messages.length > 0
                        ? consult.conversation[0].messages.find(msg => msg.sender === 'user')?.message.substring(0, 80) + '...'
                        : 'No hay conversación disponible.'}
                    </Typography>
                  </Box>
                  
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <AvatarGroup
                      max={3}
                      sx={{
                        '& .MuiAvatar-root': {
                          width: 28,
                          height: 28,
                          fontSize: '0.75rem',
                        },
                      }}
                    >
                      {consult.expertPanel &&
                        consult.expertPanel.map((expert, idx) => (
                          <Tooltip key={idx} title={expert.name || 'Experto'}>
                            <Avatar
                              sx={{
                                bgcolor: idx === 0
                                  ? 'rgba(128, 128, 128, 0.6)'
                                  : idx === 1
                                    ? 'rgba(128, 128, 128, 0.7)'
                                    : 'rgba(128, 128, 128, 0.65)',
                              }}
                            >
                              {expert.name ? expert.name.substring(0, 2).toUpperCase() : '??'}
                            </Avatar>
                          </Tooltip>
                        ))}
                    </AvatarGroup>
                    <Box display="flex" alignItems="center">
                      <Chip
                        size="small"
                        icon={<MessageCircle size={12} />}
                        label={`${consult.conversation?.reduce((total, round) => total + round.messages.length, 0) || 0} mensajes`}
                        variant="outlined"
                        sx={{ fontSize: '0.7rem' }}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        
        {consultations.length > 6 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button 
              variant="outlined" 
              endIcon={<ChevronRight />}
              onClick={onClose}
            >
              Ver todas las consultas
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <ThemeProvider theme={customTheme}>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        aria-labelledby="recent-consultations-dialog"
      >
        {/* Title + Actions */}
        <DialogTitle
          id="recent-consultations-dialog"
          sx={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Consultas Recientes
          </Typography>
          <Box>
            <Tooltip title="Buscar consulta">
              <IconButton>
                <Search size={20} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Filtrar">
              <IconButton>
                <Filter size={20} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Descargar historial">
              <IconButton>
                <Download size={20} />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>

        {/* Contenido Principal */}
        <DialogContent dividers sx={{ minHeight: '400px' }}>
          {renderMainContent()}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              color: parentTheme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : undefined,
              borderColor: parentTheme.palette.actualMode === 'wallpaper' ? 'rgba(255, 255, 255, 0.3)' : undefined
            }}
          >
            Cerrar
          </Button>
          <Button 
            variant="contained" 
            color="primary"
            startIcon={<MessageCircle />}
            onClick={onClose}
          >
            Nueva consulta
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal detalle de consulta (simplificado) */}
      {selectedConsultation && (
        <Dialog
          open={isModalOpen}
          onClose={closeDetailModal}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              {selectedConsultation.title || `Consulta del ${formatDate(selectedConsultation.createdAt)}`}
            </Typography>
            <StatusChip status={selectedConsultation.status} />
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Información de la consulta
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Fecha
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(selectedConsultation.createdAt)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Última actualización
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(selectedConsultation.updatedAt)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Panel de expertos
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {selectedConsultation.expertPanel?.map((expert, idx) => (
                  <Chip
                    key={idx}
                    avatar={
                      <Avatar sx={{
                        bgcolor: idx === 0 ? 'rgba(128, 128, 128, 0.6)' : idx === 1 ? 'rgba(128, 128, 128, 0.7)' : 'rgba(128, 128, 128, 0.65)'
                      }}>
                        {expert.name?.substring(0, 2).toUpperCase() || 'Ex'}
                      </Avatar>
                    }
                    label={`${expert.name} - ${expert.role}`}
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Conversación
            </Typography>
            {selectedConsultation.conversation && selectedConsultation.conversation.length > 0 ? (
              <Box sx={{ maxHeight: '300px', overflow: 'auto', p: 1 }}>
                {selectedConsultation.conversation.flatMap((round, roundIndex) =>
                  round.messages.map((msg, msgIndex) => {
                    const isUser = msg.sender === 'user';
                    return (
                      <Box
                        key={`${roundIndex}-${msgIndex}`}
                        sx={{
                          display: 'flex',
                          justifyContent: isUser ? 'flex-end' : 'flex-start',
                          mb: 2
                        }}
                      >
                        <Box
                          sx={{
                            maxWidth: '80%',
                            p: 2,
                            borderRadius: 2,
                            backgroundColor: isUser
                              ? 'rgba(128, 128, 128, 0.6)'
                              : isDark
                              ? '#464649'
                              : '#F2F2F7',
                            color: isUser ? 'white' : 'inherit'
                          }}
                        >
                          <Typography variant="body2" fontWeight={600} gutterBottom>
                            {isUser ? 'Tú' : getSenderName(msg.sender, selectedConsultation.expertPanel)}
                          </Typography>
                          <Typography variant="body1">{msg.message}</Typography>
                          <Typography
                            variant="caption"
                            display="block"
                            textAlign="right"
                            sx={{ mt: 1, opacity: 0.7 }}
                          >
                            {new Date(msg.timestamp).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" align="center">
                No hay mensajes disponibles para esta consulta.
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={closeDetailModal}
              variant="outlined"
              sx={{
                color: parentTheme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : undefined,
                borderColor: parentTheme.palette.actualMode === 'wallpaper' ? 'rgba(255, 255, 255, 0.3)' : undefined
              }}
            >
              Cerrar
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Download />}
              onClick={closeDetailModal}
            >
              Exportar conversación
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </ThemeProvider>
  );
}

// Helper para obtener nombre del remitente
function getSenderName(sender, experts) {
  if (sender === 'user') return 'Tú';
  
  if (sender === 'assistant1' && experts?.[0]?.name) {
    return experts[0].name;
  } else if (sender === 'assistant1') {
    return 'Estratega';
  }
  
  if (sender === 'assistant2' && experts?.[1]?.name) {
    return experts[1].name;
  } else if (sender === 'assistant2') {
    return 'Analista';
  }
  
  if (sender === 'assistant3' && experts?.[2]?.name) {
    return experts[2].name;
  } else if (sender === 'assistant3') {
    return 'Asesor';
  }
  
  return 'Asistente';
}

export default RecentConsultationsShortcut;